import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useSettings } from "../context/SettingsContext";
import Lightbox from "../components/Lightbox";
import { Maximize2 } from "lucide-react";
import { tileSpan, UNIFORM_SPAN } from "../lib/gallerySizes";

const DEFAULT_BENTO = [
  { url: "https://images.unsplash.com/photo-1774248772093-7da493634a82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHx0aW55JTIwY2FiaW4lMjBmb3Jlc3QlMjBkdXNrfGVufDB8fHx8MTc3NzcxNjAwOHww&ixlib=rb-4.1.0&q=85", span: "md:col-span-7 md:row-span-2", alt: "Cabin in dark forest", caption_mn: "Ой модны дунд тохилог байшин", caption_en: "Cozy cabin among the pines" },
  { url: "https://images.unsplash.com/photo-1758983065583-9cea714214f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjb3p5JTIwY2FiaW4lMjBpbnRlcmlvciUyMGJlZCUyMHdvb2R8ZW58MHx8fHwxNzc3NzE2MDA4fDA&ixlib=rb-4.1.0&q=85", span: "md:col-span-5", alt: "A-frame bed and landscape", caption_mn: "A-frame дотор үзэгдэх ландшафт", caption_en: "Landscape view from the A-frame" },
  { url: "https://images.unsplash.com/photo-1773423389979-b28b469967f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjb3p5JTIwY2FiaW4lMjBpbnRlcmlvciUyMGJlZCUyMHdvb2R8ZW58MHx8fHwxNzc3NzE2MDA4fDA&ixlib=rb-4.1.0&q=85", span: "md:col-span-5", alt: "Wood paneling fireplace bedroom", caption_mn: "Модон хана, дулаан гэрэлтэй өрөө", caption_en: "Wood-panelled warmth" },
  { url: "https://images.unsplash.com/photo-1769542711755-20a47abfe428?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHx0aW55JTIwY2FiaW4lMjBmb3Jlc3QlMjBkdXNrfGVufDB8fHx8MTc3NzcxNjAwOHww&ixlib=rb-4.1.0&q=85", span: "md:col-span-12", alt: "Small cabin twilight", caption_mn: "Бүрэнхий цагийн нам гүм", caption_en: "Stillness at twilight" },
  { url: "https://static.prod-images.emergentagent.com/jobs/4699da19-56e9-4739-b3c6-f66a2324e268/images/3dafb7d77473adad9dce928fa387796206d2e6e47e02732346c7c4acfcd7aef5.png", span: "md:col-span-6", alt: "Bonfire in forest", caption_mn: "Задгай галын дулаахан зэргэлдээ", caption_en: "Beside the bonfire" },
  { url: "https://static.prod-images.emergentagent.com/jobs/4699da19-56e9-4739-b3c6-f66a2324e268/images/727b670014a39e36354149e3f12570d02db3617ad31b2eb664d10bb7b8e1b527.png", span: "md:col-span-6", alt: "Sauna interior", caption_mn: "Саунгийн нам гүм", caption_en: "Quiet of the sauna" },
];

export default function GalleryPage() {
  const { t, pick, lang } = useLang();
  const { settings } = useSettings();
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  const layoutMode = settings?.gallery_layout_mode || "bento";

  useEffect(() => {
    let alive = true;
    api
      .get("/gallery")
      .then((r) => {
        if (alive) setPhotos(r.data || []);
      })
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Only fall back to default imagery AFTER the fetch settled with no photos,
  // so the page never flashes defaults and then swaps to uploads.
  const items =
    photos.length > 0
      ? photos.map((p, i) => ({
          url: p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : p.url,
          caption: pick(p, "caption"),
          alt: pick(p, "caption") || `gallery-${i}`,
          span: layoutMode === "uniform" ? UNIFORM_SPAN : tileSpan(p.tile_size || "medium"),
        }))
      : loaded
      ? DEFAULT_BENTO.map((b) => ({
          ...b,
          caption: lang === "mn" ? b.caption_mn : b.caption_en,
        }))
      : [];

  const open = (i) => setOpenIndex(i);
  const close = () => setOpenIndex(null);

  return (
    <section className="pt-28 md:pt-32 pb-20 px-5 md:px-10 max-w-7xl mx-auto" data-testid="gallery-page">
      <div className="mb-10 md:mb-14">
        <span className="text-xs tracking-[0.3em] uppercase opacity-60 block mb-3">{t("nav_gallery")}</span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">{t("gallery_title")}</h1>
        <p className="mt-4 max-w-xl opacity-80">{t("gallery_sub")}</p>
      </div>

      <div
        className="bento grid grid-cols-2 md:grid-cols-12 auto-rows-[160px] sm:auto-rows-[200px] md:auto-rows-[260px] lg:auto-rows-[280px] gap-3 md:gap-5"
        data-testid="gallery-bento"
      >
        {!loaded &&
          [
            "md:col-span-7 md:row-span-2",
            "md:col-span-5",
            "md:col-span-5",
            "md:col-span-7",
            "md:col-span-6",
            "md:col-span-6",
          ].map((span, i) => (
            <div
              key={`sk-${i}`}
              className={`col-span-2 ${span} rounded-2xl md:rounded-3xl animate-pulse`}
              style={{ background: "var(--muted)" }}
              data-testid={`gallery-skeleton-${i}`}
            />
          ))}
        {items.map((it, i) => (
          <button
            type="button"
            key={`${it.url}-${i}`}
            onClick={() => open(i)}
            className={`tile overflow-hidden rounded-2xl md:rounded-3xl relative group cursor-zoom-in text-left ${it.span || "md:col-span-6"} col-span-2 md:col-span-auto`}
            data-testid={`gallery-tile-${i}`}
            aria-label={t("view_image")}
          >
            <img
              src={it.url}
              alt={it.alt || it.caption || "gallery"}
              className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
              loading={i < 2 ? "eager" : "lazy"}
              decoding="async"
              draggable={false}
            />
            {/* Hover overlay (opacity-only transition is GPU-cheap) */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out pointer-events-none"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.15) 55%, rgba(0,0,0,0))",
              }}
            />
            {/* Hover icon top-right */}
            <span
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-out pointer-events-none"
              style={{
                background: "rgba(255,255,255,0.22)",
                border: "1px solid rgba(255,255,255,0.32)",
              }}
              aria-hidden="true"
            >
              <Maximize2 size={14} />
            </span>
            {/* Caption */}
            {it.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white pointer-events-none">
                <span className="font-serif text-base md:text-lg leading-snug opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out inline-block">
                  {it.caption}
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      <Lightbox items={items} openIndex={openIndex} onClose={close} />
    </section>
  );
}
